import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

// ─── Design tokens (same system as Collection Summary) ───────────
const C = {
  primary:     '#1565C0',
  primaryDark: '#0D47A1',
  primaryLight:'#E3F2FD',
  sectionBg:   '#1976D2',
  subtotalBg:  '#BBDEFB',
  altRow:      '#F5F8FF',
  white:       '#FFFFFF',
  dark:        '#1A1A2E',
  border:      '#B0BEC5',
  green:       '#1B5E20',
  red:         '#B71C1C',
  orange:      '#E65100',
  gray:        '#546E7A',
  lightGray:   '#ECEFF1',
};

// ─── Column definitions (must sum to 100%) ───────────────────────
const COL = {
  sr:      '3%',
  date:    '6%',
  type:    '7%',
  patient: '14%',
  uhid:    '6%',
  billNo:  '6%',
  payMode: '6%',
  refBy:   '10%',
  services:'10%',
  panel:   '7%',
  credit:  '5%',
  recd1:   '6%',
  recd2:   '6%',
  total:   '8%',
};

const styles = StyleSheet.create({
  page: { padding: '16pt 18pt', fontSize: 7, color: C.dark, backgroundColor: C.white },

  logo:        { width: '100%', height: 64, marginBottom: 2 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1, letterSpacing: 1 },
  reportSub:   { fontSize: 7.5, textAlign: 'center', color: C.gray, marginBottom: 6 },
  headerRule:  { height: 2, backgroundColor: C.primary, marginBottom: 8 },

  tableHead: { flexDirection: 'row', backgroundColor: C.sectionBg, borderRadius: 2 },
  th: {
    color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 6.5,
    paddingVertical: 4, paddingHorizontal: 2,
    textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#90CAF9',
  },

  sectionHeader: {
    flexDirection: 'row', backgroundColor: C.primaryLight,
    borderLeftWidth: 3, borderLeftColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginTop: 7, marginBottom: 0,
  },
  sectionTitle: { fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },
  sectionCount: { fontSize: 6.5, color: C.gray, marginLeft: 4 },

  row:     { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  rowEven: { backgroundColor: C.white },
  rowOdd:  { backgroundColor: C.altRow },
  td: {
    fontSize: 6.5, paddingVertical: 2.5, paddingHorizontal: 2,
    borderRightWidth: 0.3, borderRightColor: C.border,
  },

  subtotalRow: {
    flexDirection: 'row', backgroundColor: C.subtotalBg,
    borderTopWidth: 0.5, borderTopColor: C.primary,
    borderBottomWidth: 0.5, borderBottomColor: C.primary,
    paddingVertical: 3,
  },
  subtotalLabel: { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingHorizontal: 4 },
  subtotalAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', paddingHorizontal: 4 },

  summaryWrap: { marginTop: 14, flexDirection: 'row' },

  summaryBox: {
    flex: 1, borderWidth: 1, borderColor: C.primary, borderRadius: 4, marginRight: 8,
  },
  summaryBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  modeRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6, alignItems: 'center',
  },
  modeRowLast: {
    flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 6, alignItems: 'center',
  },
  modeLabel: { flex: 1, fontSize: 7, color: C.dark },
  modeAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right', minWidth: 65 },
  modeAmtGreen: { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.green, textAlign: 'right', minWidth: 65 },

  // Referral-wise breakdown box
  refBox: {
    flex: 1.3, borderWidth: 1, borderColor: C.primary, borderRadius: 4,
  },
  refBoxTitle: {
    backgroundColor: C.primary, color: C.white, fontFamily: 'Roboto', fontWeight: 700,
    fontSize: 7, textAlign: 'center', paddingVertical: 4,
  },
  refHeadRow: {
    flexDirection: 'row', backgroundColor: C.primaryLight,
    paddingVertical: 2, paddingHorizontal: 6, borderBottomWidth: 0.5, borderBottomColor: C.primary,
  },
  refHeadText: { fontSize: 6, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },
  refRow: {
    flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: C.border,
    paddingVertical: 3, paddingHorizontal: 6, alignItems: 'center',
  },
  refRowLast: {
    flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 6, alignItems: 'center',
    backgroundColor: C.primaryLight, borderTopWidth: 1, borderTopColor: C.primary,
  },
  refName:  { flex: 1.6, fontSize: 6.5, color: C.dark },
  refCount: { flex: 0.8, fontSize: 6.5, color: C.gray, textAlign: 'center' },
  refAmt:   { flex: 1, fontSize: 6.5, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },

  grandBox: {
    marginTop: 10, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4,
    backgroundColor: C.primaryDark,
  },
  grandRow: {
    flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 10,
    alignItems: 'center',
  },
  grandLabel: { flex: 1, fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white },
  grandAmt:   { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F' },

  creditBox: {
    marginTop: 6, borderWidth: 1, borderColor: '#FF8F00', borderRadius: 4,
    backgroundColor: '#FFF8E1',
    flexDirection: 'row', paddingVertical: 4, paddingHorizontal: 10, alignItems: 'center',
  },
  creditLabel: { flex: 1, fontSize: 7.5, color: '#E65100' },
  creditAmt:   { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: '#E65100' },

  footer: {
    position: 'absolute', bottom: 10, left: 18, right: 18,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border,
    paddingTop: 4,
  },
  footerLeft:  { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },

  emptyRow: { paddingVertical: 5, paddingHorizontal: 8 },
  emptyText: { fontSize: 6.5, color: C.gray, fontStyle: 'italic' },
});

// ─── Helpers ─────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}

// Entry time: use the stored `time` field (String); fall back to createdAt for old records
function rowTime(row) {
  if (row?.time) return String(row.time);
  if (!row?.createdAt) return '';
  const dt = new Date(row.createdAt);
  if (isNaN(dt)) return '';
  return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

function fmt(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtINR(n) { return `₹ ${fmt(n)}`; }

function nowStr() {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ─── Sub-components ──────────────────────────────────────────────
const TH = ({ w, children, align = 'center' }) => (
  <PDFText style={[styles.th, { width: w, textAlign: align }]}>{children}</PDFText>
);

const TD = ({ w, children, align = 'center', bold, color }) => (
  <PDFText style={[
    styles.td,
    { width: w, textAlign: align, color: color || C.dark },
    bold ? { fontFamily: 'Roboto', fontWeight: 700 } : {}
  ]}>
    {children ?? ''}
  </PDFText>
);

const TableHeader = () => (
  <PDFView style={styles.tableHead}>
    <TH w={COL.sr}>#</TH>
    <TH w={COL.date} align="left">Date / Time</TH>
    <TH w={COL.type} align="left">Type</TH>
    <TH w={COL.patient} align="left">Patient Name</TH>
    <TH w={COL.uhid}>UHID</TH>
    <TH w={COL.billNo}>Bill/Adm No.</TH>
    <TH w={COL.payMode}>Pay Mode</TH>
    <TH w={COL.refBy} align="left">Ref By</TH>
    <TH w={COL.services} align="left">Services</TH>
    <TH w={COL.panel} align="left">Panel</TH>
    <TH w={COL.credit} align="right">Credit</TH>
    <TH w={COL.recd1} align="right">Recd 1</TH>
    <TH w={COL.recd2} align="right">Recd 2</TH>
    <TH w={COL.total} align="right">Recd Amt.</TH>
  </PDFView>
);

const SectionHeader = ({ title, count }) => (
  <PDFView style={styles.sectionHeader}>
    <PDFText style={styles.sectionTitle}>{title}</PDFText>
    {count !== undefined && (
      <PDFText style={styles.sectionCount}>({count} record{count !== 1 ? 's' : ''})</PDFText>
    )}
  </PDFView>
);

const SubtotalRow = ({ label, amount }) => (
  <PDFView style={styles.subtotalRow}>
    <PDFText style={[styles.subtotalLabel, { flex: 1 }]}>{label}:</PDFText>
    <PDFText style={[styles.subtotalAmt, { width: COL.total }]}>{fmtINR(amount)}</PDFText>
  </PDFView>
);

const EmptySection = () => (
  <PDFView style={styles.emptyRow}>
    <PDFText style={styles.emptyText}>No records found for this period.</PDFText>
  </PDFView>
);

// ─── Main Component ───────────────────────────────────────────────
export const OpdReferralReportPdf = ({ data }) => {

  const opd     = data?.opdData?.stats         || {};
  const opdBill = data?.opdBillingData?.stats  || {};
  const referralSummary = data?.referralSummary || [];

  // ── Payment-mode totals (OPD Registration + OPD Billing combined) ──
  const billingCash   = (opd.recd_amt_cash   || 0) + (opdBill.recd_amt_cash   || 0);
  const billingBank   = (opd.recd_amt_bank   || 0) + (opdBill.recd_amt_bank   || 0);
  const billingBoth   = (opd.recd_amt_both   || 0) + (opdBill.recd_amt_both   || 0);
  const totalCredit   = (opd.recd_amt_credit || 0) + (opdBill.recd_amt_credit || 0);

  const opdTotal     = opd.recd_amt     || 0;
  const opdBillTotal = opdBill.recd_amt || 0;
  const grandTotal   = opdTotal + opdBillTotal;

  const referralGrandTotal = referralSummary.reduce((s, r) => s + (r.totalAmt || 0), 0);

  const printTime = nowStr();

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="OPD Referral Collection Report">
          <PDFPage style={styles.page} size="A4" orientation="landscape" wrap>

            {/* ══ HEADER ══════════════════════════════════════════════ */}
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>OPD REFERRAL COLLECTION REPORT</PDFText>
            <PDFText style={styles.reportSub}>Printed on: {printTime}</PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 6 }]} />

            {/* ══ TABLE HEADER ════════════════════════════════════════ */}
            <TableHeader />

            {/* ══════════════════════════════════════════════════════════
                SECTION 1 — OPD REGISTRATION
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="OPD Registration (Consultancy)" count={data?.opdData?.data?.length} />
            {data?.opdData?.data?.length > 0 ? data.opdData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">OPD</TD>
                <TD w={COL.patient} align="left">{`${row.name_suffix || ''} ${row.first_name || ''} ${row.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.adm_no_date || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.payment_mode || '-'}</TD>
                <TD w={COL.refBy} align="left" color={row.refby?.referral_name ? C.primaryDark : C.gray} bold={!!row.refby?.referral_name}>
                  {row.refby?.referral_name || 'Direct / N.A.'}
                </TD>
                <TD w={COL.services} align="left">Consultancy</TD>
                <TD w={COL.panel} align="left">{row.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.payment_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.payment_mode?.toUpperCase() === 'CREDIT' ? fmt(row.bal) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">-</TD>
                <TD w={COL.recd2} align="right">-</TD>
                <TD w={COL.total} align="right" bold>{fmt(row.recd)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="OPD Registration Total" amount={opdTotal} />

            {/* ══════════════════════════════════════════════════════════
                SECTION 2 — OPD BILLING
            ════════════════════════════════════════════════════════════ */}
            <SectionHeader title="OPD Billing" count={data?.opdBillingData?.data?.length} />
            {data?.opdBillingData?.data?.length > 0 ? data.opdBillingData.data.map((row, i) => (
              <PDFView key={i} style={[styles.row, i % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                <TD w={COL.sr} align="center">{i + 1}</TD>
                <TD w={COL.date} align="left">{`${fmtDate(row.date)}\n${rowTime(row)}`}</TD>
                <TD w={COL.type} align="left">OPD Bill</TD>
                <TD w={COL.patient} align="left">{`${row.patient?.name_suffix || ''} ${row.patient?.first_name || ''} ${row.patient?.last_name || ''}`.trim()}</TD>
                <TD w={COL.uhid} align="center">{row.uhid || '-'}</TD>
                <TD w={COL.billNo} align="center">{row.patient?.adm_no_date || '-'}</TD>
                <TD w={COL.payMode} align="center">{row.pay_mode || '-'}</TD>
                <TD w={COL.refBy} align="left" color={row.patient?.ref_by?.referral_name ? C.primaryDark : C.gray} bold={!!row.patient?.ref_by?.referral_name}>
                  {row.patient?.ref_by?.referral_name || 'Direct / N.A.'}
                </TD>
                <TD w={COL.services} align="left">
                  {(row.combinedServiceDetails || []).map(s => s.service_name || s.test_name).filter(Boolean).join(', ') || '-'}
                </TD>
                <TD w={COL.panel} align="left">{row.patient?.sponsor?.name || '-'}</TD>
                <TD w={COL.credit} align="right" color={row.pay_mode?.toUpperCase() === 'CREDIT' ? C.orange : C.dark}>
                  {row.pay_mode?.toUpperCase() === 'CREDIT' ? fmt(row.balance_amt) : '-'}
                </TD>
                <TD w={COL.recd1} align="right">
                  {row.pay_mode_1 ? `${row.pay_mode_1} ${fmt(row.rec_amt_1)}` : (row.rec_amt_1 ? fmt(row.rec_amt_1) : '-')}
                </TD>
                <TD w={COL.recd2} align="right">
                  {row.pay_mode_2 ? `${row.pay_mode_2} ${fmt(row.rec_amt_2)}` : (row.rec_amt_2 ? fmt(row.rec_amt_2) : '-')}
                </TD>
                <TD w={COL.total} align="right" bold>{fmt(row.total_recd)}</TD>
              </PDFView>
            )) : <EmptySection />}
            <SubtotalRow label="OPD Billing Total" amount={opdBillTotal} />

            {/* ══════════════════════════════════════════════════════════
                SUMMARY SECTION
            ════════════════════════════════════════════════════════════ */}
            <PDFView style={[styles.headerRule, { marginTop: 16, marginBottom: 0, height: 1.5 }]} />
            <PDFText style={{ fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, marginTop: 6, marginBottom: 4 }}>
              COLLECTION SUMMARY
            </PDFText>

            <PDFView style={styles.summaryWrap}>

              {/* Box 1 — Payment Mode Breakdown */}
              <PDFView style={styles.summaryBox}>
                <PDFText style={styles.summaryBoxTitle}>PAYMENT MODE BREAKDOWN</PDFText>

                <PDFView style={[styles.modeRow, { backgroundColor: '#E8F5E9' }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.green }]}>Cash</PDFText>
                  <PDFText style={styles.modeAmtGreen}>{fmtINR(billingCash)}</PDFText>
                </PDFView>
                <PDFView style={[styles.modeRow, { backgroundColor: '#E3F2FD' }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.primary }]}>
                    Bank / Digital
                  </PDFText>
                  <PDFText style={styles.modeAmt}>{fmtINR(billingBank)}</PDFText>
                </PDFView>
                {billingBoth > 0 && (
                  <PDFView style={[styles.modeRow, { backgroundColor: '#FFF9C4' }]}>
                    <PDFText style={[styles.modeLabel, { color: '#F57F17' }]}>Both Cash+Bank</PDFText>
                    <PDFText style={[styles.modeAmt, { color: '#F57F17' }]}>{fmtINR(billingBoth)}</PDFText>
                  </PDFView>
                )}
                {totalCredit > 0 && (
                  <PDFView style={[styles.modeRow, { backgroundColor: '#FFF3E0' }]}>
                    <PDFText style={[styles.modeLabel, { color: C.orange }]}>Credit (Outstanding)</PDFText>
                    <PDFText style={[styles.modeAmt, { color: C.orange }]}>{fmtINR(totalCredit)}</PDFText>
                  </PDFView>
                )}
                <PDFView style={[styles.modeRowLast, { backgroundColor: '#1B5E20' }]}>
                  <PDFText style={[styles.modeLabel, { fontFamily: 'Roboto', fontWeight: 700, color: C.white }]}>NET COLLECTED</PDFText>
                  <PDFText style={[styles.modeAmt, { color: '#A5D6A7' }]}>{fmtINR(grandTotal)}</PDFText>
                </PDFView>
              </PDFView>

              {/* Box 2 — Referral-wise Breakdown */}
              <PDFView style={styles.refBox}>
                <PDFText style={styles.refBoxTitle}>COLLECTION BY REFERRAL</PDFText>
                <PDFView style={styles.refHeadRow}>
                  <PDFText style={[styles.refHeadText, { flex: 1.6 }]}>Referral Name</PDFText>
                  <PDFText style={[styles.refHeadText, { flex: 0.8, textAlign: 'center' }]}>Patients</PDFText>
                  <PDFText style={[styles.refHeadText, { flex: 1, textAlign: 'right' }]}>Amount</PDFText>
                </PDFView>
                {referralSummary.length > 0 ? referralSummary.map((r, i) => (
                  <PDFView key={i} style={[styles.refRow, i % 2 === 0 ? { backgroundColor: C.white } : { backgroundColor: C.altRow }]}>
                    <PDFText style={styles.refName}>{r.referral_name}</PDFText>
                    <PDFText style={styles.refCount}>{r.opdCount + r.opdBillCount}</PDFText>
                    <PDFText style={styles.refAmt}>{fmtINR(r.totalAmt)}</PDFText>
                  </PDFView>
                )) : (
                  <PDFView style={styles.emptyRow}><PDFText style={styles.emptyText}>No referral data available.</PDFText></PDFView>
                )}
                <PDFView style={styles.refRowLast}>
                  <PDFText style={[styles.refName, { fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark }]}>Total</PDFText>
                  <PDFText style={styles.refCount}></PDFText>
                  <PDFText style={[styles.refAmt, { color: C.primaryDark }]}>{fmtINR(referralGrandTotal)}</PDFText>
                </PDFView>
              </PDFView>

            </PDFView>

            {/* Grand Total Banner */}
            <PDFView style={styles.grandBox}>
              <PDFView style={styles.grandRow}>
                <PDFText style={styles.grandLabel}>
                  GRAND TOTAL COLLECTED  (OPD Registration + OPD Billing)
                </PDFText>
                <PDFText style={styles.grandAmt}>{fmtINR(grandTotal)}</PDFText>
              </PDFView>
            </PDFView>

            {/* Credit Outstanding Notice */}
            {totalCredit > 0 && (
              <PDFView style={styles.creditBox}>
                <PDFText style={styles.creditLabel}>
                  * Credit Outstanding — on account, NOT collected (Cash/Bank flow excluded):
                </PDFText>
                <PDFText style={styles.creditAmt}>{fmtINR(totalCredit)}</PDFText>
              </PDFView>
            )}

            {/* ══ PAGE FOOTER ══════════════════════════════════════════ */}
            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>OPD Referral Collection Report  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>

          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default OpdReferralReportPdf;
